import {
  Outlet,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useEffect, useState } from "react";
import Register from "../pages/auth/Register";
import Login from "../pages/auth/Login";
import ForgotPassword from "../pages/auth/ForgotPassword";
import AccountVerificationSent from "../pages/auth/AccountVerificationSent";
import AccountLinkExpired from "../components/auth/AccountLinkExpired";
import ActivateLink from "../components/auth/ActivateLink";
import ForgotPasswordLinkAlreadyUsedPage from "../components/auth/ForgotPasswordLinkAlreadyUsedPage";
import ForgotPasswordSuccessPage from "../components/auth/ForgotPasswordSuccessPage";
import ForgotPasswordChangePasswordForm from "../components/auth/ForgotPasswordChangePasswordForm";
import TokenHandlerComponent from "../pages/auth/TokenHandler";
import DataLoader from "../widgets/DataLoader";

const AuthBaseLayout = () => {
  const location = useLocation();
  const [hasAccessToken, setHasAccessToken] = useState<boolean | null>(null);
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");

  const checkAuth = () => {
    const accessToken = localStorage.getItem("auth_token");
    const hasToken = accessToken !== null;
    console.log("[AuthBaseLayout] Checking auth - Token exists:", hasToken);
    setHasAccessToken(hasToken);
  };

  console.log("status", status);

  useEffect(() => {
    console.log("[AuthBaseLayout] Location changed:", location.pathname);
    // For /dataloader route, set hasAccessToken to true to allow token processing
    // without triggering redirects (TokenHandler will handle auth flow)
    if (location.pathname.startsWith("/dataloader")) {
      console.log("[AuthBaseLayout] On /dataloader route, allowing token processing");
      setHasAccessToken(true);
      return;
    }
    checkAuth();
  }, [location]); // Only re-check when location changes, not hasAccessToken

  if (hasAccessToken === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <DataLoader />
      </div>
    );
  }

  // console.log("location.pathname", location.pathname)

  if (location.pathname.startsWith("/login")) {
    return <Login />;
  }

  if (location.pathname.startsWith("/register")) {
    return <Register />;
  }

  if (location.pathname.startsWith("/forgot-password")) {
    return <ForgotPassword />;
  }

  if (location.pathname.startsWith("/dataloader")) {
    return <TokenHandlerComponent />;
  }

  // Handle account verification with different statuses
  if (location.pathname === "/account_verification_sent") {
    // console.log("status query param:", status);

    if (status === "expired") {
      return <AccountLinkExpired />;
    } else if (status === "already_used") {
      return <AccountLinkExpired />;
    } else if (status === "used") {
      return <ActivateLink />;
    } else if (status === "invalid") {
      return <ForgotPasswordLinkAlreadyUsedPage />;
    } else if (!status) {
      // Default case when no status is provided
      return <AccountVerificationSent />;
    } else {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-800 flex items-center justify-center p-4">
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8 border border-white/20 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Unknown Status
            </h2>
            <p className="text-gray-600">Status: {status}</p>
          </div>
        </div>
      );
    }
  }

  if (location.pathname.startsWith("/dataloader")) {
    return <TokenHandlerComponent />;
  }

  if (location.pathname === "/link-used-forgot-password") {
    return <ForgotPasswordLinkAlreadyUsedPage />;
  }

  if (location.pathname === "/link-expired-forgot-password") {
    return <AccountLinkExpired />;
  }

  if (location.pathname.endsWith("/revoked")) {
    return <AccountLinkExpired />;
  }

  if (location.pathname === "/forgot_password_verification_sent") {
    return <ForgotPasswordSuccessPage />;
  }

  if (location.pathname.startsWith("/reset_password_with_forgot_pwd_link")) {
    return <ForgotPasswordChangePasswordForm />;
  }

  if (
    location.pathname.startsWith(
      "/inform_forgot_password_verification_link_status"
    )
  ) {
    return <AccountVerificationSent />;
  }

  if (location.pathname === "/user_invite_request_error") {
    return <AccountLinkExpired />;
  }

  // Redirect to login if no token (use Navigate component, not navigate function)
  // Double-check localStorage directly to avoid race conditions with state updates
  if (!hasAccessToken && !localStorage.getItem("auth_token")) {
    // console.log("[AuthBaseLayout] No token found, redirecting to login");
    // return <Navigate to="/login" replace />;
    return <Outlet />
  }

  console.log("[AuthBaseLayout] Token validated, rendering protected routes");
  return <Outlet />;
};

export default AuthBaseLayout;
